import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { api, downloadJson } from './api.js';
import ExplainableRun from './ExplainableRun.jsx';
import JsonReceipt from './JsonReceipt.jsx';
import { isCurrentStackEngine } from '../lib/current-stack.js';

const LAYERS = [
  ['sources', 'Sources'],
  ['blocks', 'Raw Blocks'],
  ['actors', 'Actors'],
  ['help', 'Help'],
  ['workflow', 'Workflow'],
  ['temporal', 'Deadlines'],
  ['accountability', 'Accountability'],
  ['overrides', 'Overrides'],
  ['definitions', 'Definitions'],
  ['coverage', 'Coverage + Validation'],
];

function StatusBadge({ value }) {
  const normalized = String(value || 'unknown').toLowerCase();
  const kind =
    normalized.includes('reject') ||
    normalized.includes('fail') ||
    normalized.includes('error') ||
    normalized.includes('incomplete')
      ? 'bad'
      : normalized.includes('control') ||
          normalized.includes('stale') ||
          normalized.includes('pending') ||
          normalized.includes('progress') ||
          normalized.includes('legacy') ||
          normalized.includes('partial') ||
          normalized.includes('review')
        ? 'warn'
        : normalized.includes('complete') ||
            normalized.includes('admissible') ||
            normalized.includes('pass') ||
            normalized.includes('confirmed') ||
            normalized.includes('operational')
          ? 'good'
          : 'neutral';
  return <span className={`badge badge-${kind}`}>{String(value || 'unknown')}</span>;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function cellText(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function DataTable({ rows }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const columns = useMemo(() => {
    const keys = [];
    safeRows.slice(0, 20).forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!keys.includes(key)) keys.push(key);
      });
    });
    return keys.slice(0, 12);
  }, [safeRows]);

  if (!safeRows.length) return <div className="empty-state">No stored rows returned for this corpus surface.</div>;

  return (
    <div className="table-wrap">
      <table>
        <thead><tr>{columns.map((column) => <th key={column}>{column.replaceAll('_', ' ')}</th>)}</tr></thead>
        <tbody>
          {safeRows.map((row, index) => (
            <tr key={row.id || row.source_content_id || row.manifest_id || index}>
              {columns.map((column) => (
                <td key={column} title={cellText(row[column])}>{cellText(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LayerLoading() {
  return <div className="loading-panel" role="status">Loading corpus layer…</div>;
}

function LayerError({ error }) {
  return <div className="error-panel" role="alert">{error?.message || 'Corpus layer could not be loaded.'}</div>;
}

export function CorpusLayers() {
  const [selected, setSelected] = useState('sources');
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let active = true;
    setState({ loading: true });
    api(`/api/layers/${selected}`)
      .then((data) => active && setState({ data }))
      .catch((error) => active && setState({ error }));
    return () => {
      active = false;
    };
  }, [selected]);

  const datasets = state.data
    ? Object.entries(state.data).filter(([key, value]) => key !== 'legacy_gap' && Array.isArray(value))
    : [];
  const rowCount = datasets.reduce((total, [, rows]) => total + rows.length, 0);

  return (
    <>
      <header className="page-header">
        <div>
          <div className="eyebrow">AUDIT / FROZEN CORPUS</div>
          <h1>Historical 2.5.39 corpus layers</h1>
          <p>
            Inspect preserved 2.5.39 layer tables. Current law evidence is available in the source-bound Layer Viewer.
          </p>
        </div>
        <div className="page-actions">
          {state.data && (
            <button className="button" onClick={() => downloadJson(`rosetta-corpus-${selected}.json`, state.data)} type="button">
              Export layer
            </button>
          )}
          <NavLink className="button button-primary" to="/layers">Inspect current law</NavLink>
        </div>
      </header>

      <div className="gap-notice">
        <strong>This frozen campaign is historical evidence.</strong>
        <p>
          This page reads the preserved 2.5.39 campaign. The current Layer Viewer preserves exact source, attempt, and validation identity.
        </p>
      </div>

      <div className="layer-layout">
        <nav className="layer-nav panel" aria-label="Corpus Rosetta layers">
          {LAYERS.map(([key, label]) => (
            <button key={key} className={selected === key ? 'active' : ''} onClick={() => setSelected(key)} type="button">
              <span>{label}</span>
              <small>{key}</small>
            </button>
          ))}
        </nav>

        <section className="panel layer-content">
          <div className="section-heading">
            <div>
              <span className="eyebrow">HISTORICAL 2.5.39 CORPUS VIEW</span>
              <h2>{LAYERS.find(([key]) => key === selected)?.[1]}</h2>
            </div>
            {!state.loading && !state.error && <span className="mono">{rowCount.toLocaleString()} RETURNED ROWS</span>}
          </div>

          {state.loading && <LayerLoading />}
          {state.error && <LayerError error={state.error} />}
          {state.data?.legacy_gap && (
            <div className="gap-notice">
              <strong>Explicit schema gap</strong>
              <p>{state.data.legacy_gap}</p>
            </div>
          )}
          {datasets.map(([key, rows]) => (
            <div className="dataset" key={key}>
              <div className="dataset-heading">
                <h3>{key.replaceAll('_', ' ')}</h3>
                <span className="mono">{rows.length.toLocaleString()} ROWS</span>
              </div>
              <DataTable rows={rows} />
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

export function CurrentRunDetail() {
  const { runId } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let active = true;
    const parsed = Number(runId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setState({ error: new Error('Invalid extraction run reference.') });
      return () => {};
    }

    setState({ loading: true });
    api(`/api/runs/${parsed}`)
      .then((payload) => {
        if (!active) return;
        const receipt = payload?.run_receipt;
        const engine = receipt?.engine_version || payload?.law_view?.engine_version;
        if (!receipt) throw new Error('Run receipt was not returned.');
        setState({
          data: payload,
          currentStack: Boolean(engine && isCurrentStackEngine(engine)),
          engine,
        });
      })
      .catch((error) => active && setState({ error }));

    return () => {
      active = false;
    };
  }, [runId]);

  if (state.loading) return <div className="loading-panel" role="status">Retrieving extraction run {runId}…</div>;
  if (state.error) return <div className="error-panel" role="alert">{state.error.message}</div>;

  if (!state.currentStack) {
    return (
      <section className="panel">
        <div className="eyebrow">CURRENT WORKSPACE BOUNDARY</div>
        <h1>This saved run is outside the current Rosetta stack.</h1>
        <p>
          The run remains preserved, but it is not substituted into the current workspace. Open the current pipeline to select the current source revision and attempt.
        </p>
        <div className="page-actions">
          <NavLink className="button button-primary" to="/pipeline">Open current pipeline</NavLink>
          <NavLink className="button" to="/layers">Open corpus layers</NavLink>
        </div>
      </section>
    );
  }

  const payload = state.data;
  const receipt = payload.run_receipt;
  const run = payload.law_view;
  const document = receipt?.source_document || {};

  if (!run) {
    return (
      <>
        <header className="page-header">
          <div>
            <div className="eyebrow">CURRENT EXTRACTION RUN {receipt.id}</div>
            <h1>{document.document_name || `Source document ${receipt.source_document_id}`}</h1>
            <p>{receipt.lifecycle_detail || 'No canonical five-layer output is attached to this current-stack run.'}</p>
          </div>
          <div className="page-actions">
            <button className="button" onClick={() => downloadJson(`rosetta-run-${receipt.id}.json`, payload)} type="button">
              Export stored state
            </button>
          </div>
        </header>

        <section className="receipt-strip">
          <div><span>Engine</span><strong>{state.engine}</strong></div>
          <div><span>Lifecycle</span><StatusBadge value={receipt.lifecycle_label} /></div>
          <div><span>Stored status</span><StatusBadge value={receipt.run_status} /></div>
          <div><span>Created</span><strong>{formatDate(receipt.created_at)}</strong></div>
        </section>

        <div className="gap-notice">
          <strong>No five-layer output is attached to this run.</strong>
          <p>The preserved current-stack receipt remains visible without inventing a completed decomposition.</p>
        </div>

        <ExplainableRun payload={payload} receipt={receipt} run={null} />

        <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">STORED RUN</span><h2>Receipt state</h2></div></div>
          <DataTable rows={[receipt]} />
        </section>
        {payload.source_receipt && (
          <section className="panel">
            <div className="section-heading"><div><span className="eyebrow">SOURCE</span><h2>Immutable source receipt</h2></div></div>
            <DataTable rows={[payload.source_receipt]} />
          </section>
        )}
      </>
    );
  }

  const grouped = (run.objects || []).reduce((accumulator, object) => {
    const key = object.layer || 'unknown';
    if (!accumulator[key]) accumulator[key] = [];
    accumulator[key].push(object);
    return accumulator;
  }, {});

  return (
    <>
      <header className="page-header">
        <div>
          <div className="eyebrow">CURRENT EXTRACTION RUN {run.extraction_run_id}</div>
          <h1>{run.document_name || document.document_name || `Run ${runId}`}</h1>
          <p>Source identifier {run.document_identifier || document.document_identifier || 'unavailable'} · version {run.source_version || 'unavailable'}</p>
        </div>
        <div className="page-actions">
          <NavLink className="button" to="/pipeline">← Pipeline</NavLink>
          <NavLink className="button" to="/layers">Corpus layers</NavLink>
          <button className="button button-primary" onClick={() => downloadJson(`rosetta-run-${run.extraction_run_id}.json`, payload)} type="button">
            Export full receipt
          </button>
        </div>
      </header>

      <section className="receipt-strip">
        <div><span>Engine</span><strong>{state.engine}</strong></div>
        <div><span>Lifecycle</span><StatusBadge value={receipt.lifecycle_label} /></div>
        <div><span>Provenance</span><StatusBadge value={run.provenance_state} /></div>
        <div><span>Admissibility</span><StatusBadge value={run.admissibility_state} /></div>
        <div><span>Completed</span><strong>{formatDate(run.completed_at)}</strong></div>
      </section>

      <ExplainableRun payload={payload} receipt={receipt} run={run} />

      <section className="panel hash-ledger">
        {[
          ['Source identity', run.source_identity_hash],
          ['Source content', run.source_content_hash],
          ['Source bytes', run.source_byte_hash],
          ['Rule manifest', run.rule_manifest_hash],
          ['Configuration', run.configuration_hash],
          ['Output content', run.output_content_hash],
        ].map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <code>{value || 'not available'}</code>
          </div>
        ))}
      </section>

      <section className="coverage-grid">
        {Object.entries(run.coverage || {}).map(([layer, coverageReceipt]) => (
          <article className="coverage-card" key={layer}>
            <span>{layer.toUpperCase()}</span>
            <StatusBadge value={coverageReceipt.status} />
            <p>{coverageReceipt.reason}</p>
          </article>
        ))}
      </section>

      {Object.entries(grouped).map(([layer, objects]) => (
        <section className="panel" key={layer}>
          <div className="section-heading">
            <div><span className="eyebrow">CANONICAL LAYER</span><h2>{layer.toUpperCase()}</h2></div>
            <span className="mono">{objects.length} OBJECTS</span>
          </div>
          <div className="object-list">
            {objects.map((object, index) => (
              <article className="object-card" key={`${object.source_object_type || layer}-${object.source_object_id || index}`}>
                <div className="object-topline">
                  <strong>{object.source_object_type || layer}</strong>
                  <StatusBadge value={object.confirmed ? 'confirmed' : 'review required'} />
                </div>
                <JsonReceipt
                  value={object.normalized_value}
                  filename={`rosetta-object-${object.source_object_id || index}.json`}
                  label="Complete normalized object JSON"
                />
                <div className="mono object-footer">SOURCE BLOCK {object.source_block_id || '—'} · CONFIDENCE {object.confidence ?? '—'}</div>
              </article>
            ))}
          </div>
        </section>
      ))}

      <section className="panel">
        <div className="section-heading"><div><span className="eyebrow">VALIDATION</span><h2>Receipt tests</h2></div></div>
        <DataTable rows={payload.validation_results || []} />
      </section>
    </>
  );
}
